import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Phone, Rocket, IndianRupee, Info, UserCog, Sparkles } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Logo from "@/components/Logo";
import StreamsMenu from "@/components/nav/StreamsMenu";
import PredictorPickerButton from "@/components/nav/PredictorPickerButton";

/**
 * Public landing Navbar — Feb 2026 sleek rewrite (v3).
 *
 * v3 changes (Feb 17, landing revamp):
 *   • Top-nav now carries a "Streams" dropdown → Engineering | Medical,
 *     each with a nested sub-menu (Top Colleges, Branches / Explainer,
 *     CAP Rounds Info, FAQs). This is where Colleges / Branches / CAP
 *     Guide live now that the landing page was slimmed.
 *   • Desktop primary links: How it works · Streams · Pricing.
 *   • Burger drawer's mobile menu embeds the streams as two collapsible
 *     groups so parents on phones don't need to hover.
 */
const PRIMARY = [
  { key: "howItWorks", id: "how-it-works", label: "How it works", Icon: Rocket, accent: "amber" },
  { key: "pricing",    id: "pricing",     label: "Pricing",       Icon: IndianRupee, accent: "emerald" },
];
const MOBILE_EXTRA = [
  { key: "about", id: "about-us", label: "About us", Icon: Info, accent: "slate" },
];

const ACCENT = {
  amber:   { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-800" },
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800" },
  slate:   { bg: "bg-slate-100",  border: "border-slate-200",   text: "text-slate-700" },
  violet:  { bg: "bg-violet-50",  border: "border-violet-200",  text: "text-violet-800" },
  sky:     { bg: "bg-sky-50",     border: "border-sky-200",     text: "text-sky-800" },
  rose:    { bg: "bg-rose-50",    border: "border-rose-200",    text: "text-rose-800" },
};

function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - 72;
  window.scrollTo({ top, behavior: "smooth" });
}

export default function Navbar({ onOpenAuth }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Toggle a body-level class while the burger drawer is open — used by
  // global CSS to hide the AISensy floating chat bubble so it doesn't
  // intercept taps on the drawer's right column (mobile especially).
  useEffect(() => {
    if (mobileOpen) document.body.classList.add("nav-drawer-open");
    else document.body.classList.remove("nav-drawer-open");
    return () => document.body.classList.remove("nav-drawer-open");
  }, [mobileOpen]);

  const goSection = (id) => {
    setMobileOpen(false);
    if (window.location.pathname !== "/") {
      navigate(`/#${id}`);
      setTimeout(() => scrollToId(id), 80);
    } else {
      scrollToId(id);
    }
  };

  return (
    <header
      className={`sticky top-0 z-40 backdrop-blur bg-white/85 border-b transition-shadow pt-[env(safe-area-inset-top,0px)] ${scrolled ? "border-slate-200 shadow-[0_1px_0_rgba(15,23,42,0.04)]" : "border-transparent"}`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto h-16 px-4 sm:px-6 flex items-center gap-3">
        <Logo to="/" size="md" testId="nav-logo" className="-ml-1" />

        {/* Primary desktop links */}
        <ul className="hidden md:flex items-center gap-1 ml-4" data-testid="nav-links">
          <li>
            <button
              onClick={() => goSection("how-it-works")}
              className="h-9 px-3 rounded-md text-[13px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              data-testid="nav-link-howItWorks"
            >
              {t("nav.howItWorks", "How it works")}
            </button>
          </li>
          <li><StreamsMenu /></li>
          <li>
            <button
              onClick={() => goSection("pricing")}
              className="h-9 px-3 rounded-md text-[13px] font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              data-testid="nav-link-pricing"
            >
              {t("nav.pricing", "Pricing")}
            </button>
          </li>
        </ul>

        <div className="ml-auto flex items-center gap-1.5">
          <LanguageSwitcher className="hidden sm:inline-flex" />

          {/*
            Register-free CTA — desktop only. Kept OUT of the mobile bar to
            avoid competing with "Sign in" and the hamburger on narrow
            screens. The gentle shimmer + Sparkles icon calls attention
            without shouting: parents who are scanning learn there IS a
            way in that costs nothing.
          */}
          <button
            onClick={() => { try { window.__preferSignup = true; } catch (_) { /* noop */ } onOpenAuth?.("signup"); }}
            className="hidden md:inline-flex relative overflow-hidden items-center h-9 px-4 rounded-md text-[13px] font-bold text-white bg-gradient-to-r from-violet-600 via-fuchsia-600 to-rose-500 hover:from-violet-500 hover:to-rose-400 shadow-sm hover:shadow-lg transition-all whitespace-nowrap group ring-1 ring-white/20"
            data-testid="nav-register-free"
            title="Free — no payment to explore your predictions"
          >
            <span
              aria-hidden="true"
              className="absolute inset-y-0 -left-3/4 w-1/2 skew-x-12 bg-white/25 blur-sm animate-nav-shine pointer-events-none"
            />
            <Sparkles className="w-3.5 h-3.5 mr-1.5 relative z-10 group-hover:animate-pulse" />
            <span className="relative z-10">Register free</span>
          </button>

          {/* SPEC — Predictor picker button (Feb 2026 v2).
              Always-shining outlined pill with a dropdown offering both
              live predictors (Engineering + NEET). Replaces the older
              single "NEET predictor" green pill so both exam paths get
              equal top-bar prominence. */}
          <div className="hidden sm:block">
            <PredictorPickerButton />
          </div>

          <button
            onClick={onOpenAuth}
            className="inline-flex items-center h-9 px-3 sm:px-4 rounded-md text-[13px] font-bold bg-amber-500 text-slate-900 hover:bg-amber-400 transition-colors whitespace-nowrap"
            data-testid="nav-student-login"
            title="For students and parents"
          >
            <span className="sm:hidden">Sign in</span>
            <span className="hidden sm:inline">Student login</span>
          </button>
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="inline-flex items-center justify-center h-9 w-9 rounded-md text-slate-700 hover:bg-slate-100"
            aria-label="Open menu"
            aria-expanded={mobileOpen}
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div
          className="border-t border-slate-200 bg-white shadow-lg animate-in fade-in slide-in-from-top-2 duration-200"
          data-testid="nav-mobile-drawer"
          role="dialog"
          aria-label="Site menu"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
            {/* Primary tiles — How it works, Pricing, About */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              {[...PRIMARY, ...MOBILE_EXTRA].map((s) => {
                const a = ACCENT[s.accent] || ACCENT.slate;
                return (
                  <button
                    key={s.key}
                    onClick={() => goSection(s.id)}
                    className="group text-left rounded-xl border border-slate-200 bg-white hover:border-slate-400 hover:shadow-sm transition-all p-3 flex items-center gap-3"
                    data-testid={`nav-mobile-link-${s.key}`}
                  >
                    <span className={`w-9 h-9 rounded-lg border ${a.border} ${a.bg} ${a.text} flex items-center justify-center flex-shrink-0`}>
                      <s.Icon className="w-4 h-4" />
                    </span>
                    <span className="font-display text-sm font-semibold text-slate-900 truncate">
                      {t(`nav.${s.key}`, s.label)}
                    </span>
                  </button>
                );
              })}
            </div>

            {/*
              Feb 2026 v2: the "Streams" dropdown is already in the top
              nav on every screen size, so we no longer render the
              MobileStreamsGroups block here — it just duplicated what the
              user could reach from the top bar. Keeps the drawer short
              and scan-friendly (parents mostly want How-it-works /
              Pricing / About / Call).
            */}

            {/* Language switcher (mobile only, since it's hidden in the top bar) */}
            <div className="sm:hidden mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-3">
              <span className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Language</span>
              <LanguageSwitcher />
            </div>

            {/* Call CTA — wide bar */}
            <a
              href="tel:+912048564281"
              className="mt-3 flex items-center justify-center gap-2 h-11 rounded-lg text-sm font-semibold text-slate-900 bg-amber-100 hover:bg-amber-200 border border-amber-300 transition-colors"
              data-testid="nav-mobile-phone"
            >
              <Phone className="w-4 h-4" /> Call us · 020 4856 4281
            </a>

            {/* Partner / Admin login — subtle secondary */}
            <a
              href="/portal/login"
              onClick={() => setMobileOpen(false)}
              className="mt-2 flex items-center justify-center gap-2 h-10 rounded-lg text-[13px] font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors"
              data-testid="nav-mobile-partner-login"
            >
              <UserCog className="w-3.5 h-3.5" /> Partner / Admin login
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
